/**
 * 题目形状。题型是一张映射表，不是一个类层次。
 * DataMap 是内存里的载荷真源，SCHEMAS 是题块 YAML 的线上校验，两者只在 parsing 处相接。
 * 只依赖 ids，其余核心模块都从这里取形状与线上校验，reading 的递归子题线上形状在 QuestionWire 里。
 * 不判分，答案文本与得分算法在 review/question-types 的 descriptor 里。
 */

import { z } from 'zod'
import type { ConceptId, QuestionId } from './ids'

/** 填空接受哪种写法归一化。只有这四种，函数定义在 question-types 的 NORMALIZERS 里，一份。 */
export const NormalizerSchema = z.enum(['trim', 'case', 'width', 'punct'])

export type Normalizer = z.infer<typeof NormalizerSchema>

export interface ChoiceData {
	readonly options: readonly [string, string, ...string[]]
	readonly answer: number
}

export interface MultiChoiceData {
	readonly options: readonly [string, string, ...string[]]
	readonly answers: readonly number[]
}

export interface TrueFalseData {
	/** prompt 就是那句断言，expects 说明断言真假。 */
	readonly expects: boolean
}

/**
 * `normalize` 在域内是必填的空可省数组：线上缺省由 SCHEMAS 补成 []，
 * 此后判分代码不再区分「没写」和「写了空数组」，所以这里没有可选字段。
 */
export interface BlankSpec {
	readonly accepts: readonly [string, ...string[]]
	readonly normalize: readonly Normalizer[]
}

export interface BlankData {
	/** `___` 的数量必须等于 blanks 的长度，这条规则写在 schema 的 refine 里。 */
	readonly text: string
	readonly blanks: readonly [BlankSpec, ...BlankSpec[]]
}

export interface OrderData {
	readonly items: readonly [string, string, ...string[]]
	/** 正确顺序的下标排列，与 items 等长、不重不漏。 */
	readonly answer: readonly number[]
}

export interface MatchData {
	readonly pairs: readonly [readonly [string, string], ...[string, string][]]
}

export interface FreeRecallData {
	readonly reference: string
	readonly keywords: readonly string[]
}

/** 组合题。子题不可能是 reading，所以两层嵌套在类型层面就构造不出来。 */
export interface ReadingData {
	readonly passage: string
	readonly questions: readonly [Question<LeafKind>, ...Question<LeafKind>[]]
}

export interface DataMap {
	choice: ChoiceData
	multiChoice: MultiChoiceData
	trueFalse: TrueFalseData
	blank: BlankData
	order: OrderData
	match: MatchData
	freeRecall: FreeRecallData
	reading: ReadingData
}

/** UI 一次交互能给出的值。渲染器与评分器共用这张表，不另设中间类型。 */
export interface AnswerMap {
	choice: number
	multiChoice: readonly number[]
	trueFalse: boolean
	/** 与 data.blanks 同序，未作答为空字符串。 */
	blank: readonly string[]
	order: readonly number[]
	/** 左项文本到所选右项文本。 */
	match: Readonly<Record<string, string>>
	freeRecall: string
	reading: readonly QuestionAnswer[]
}

export type QuestionKind = keyof DataMap

export type LeafKind = Exclude<QuestionKind, 'reading'>

export interface Question<K extends QuestionKind = QuestionKind> {
	readonly id: QuestionId
	readonly conceptId: ConceptId
	readonly kind: K
	readonly prompt: string
	readonly explain: string | null
	readonly data: DataMap[K]
	/** 指向所属 Concept.sections 的下标，题目归属段落只有这一处记录。 */
	readonly sectionIndex: number
	readonly sourceLine: number
}

export type AnyQuestion = { [K in QuestionKind]: Question<K> }[QuestionKind]

/** 答案按 kind 带标签，所以 kind 与 value 对不上这在类型层面构造不出来。 */
export type QuestionAnswer = { [K in QuestionKind]: { readonly kind: K; readonly value: AnswerMap[K] } }[QuestionKind]

export type AnswerValueOf<K extends QuestionKind> = AnswerMap[K]

/** `___` 的数量，blank 题的数量一致性检查用。唯一一处数占位符的代码。 */
export function countBlankMarkers(text: string): number {
	const markers = text.match(/_{3}/g)
	return markers === null ? 0 : markers.length
}

/**
 * 题块 YAML 里 `data` 字段的线上校验。
 * schema 管范围与跨字段一致性；元组形状由 parsing.parseQuestion 在长度检查之后构造，构造点唯一，
 * 所以这里不做 `satisfies` 到 DataMap 的绑定，域内类型才是载荷真源。
 * reading 只校验外壳，子题由 parseQuestion 递归构造，所以这里不需要自引用。
 */
export const SCHEMAS = {
	choice: z
		.object({ options: z.array(z.string().min(1)).min(2).max(8), answer: z.number().int().nonnegative() })
		.refine((d) => d.options.every((o, i) => !d.options.some((p, j) => j < i && p === o)), { message: 'options 有重复项' })
		.refine((d) => d.answer < d.options.length, { message: 'answer 下标越界' }),
	multiChoice: z
		.object({
			options: z.array(z.string().min(1)).min(2).max(8),
			answers: z.array(z.number().int().nonnegative()).min(1).max(8),
		})
		.refine((d) => d.answers.every((i) => i < d.options.length), { message: 'answers 下标越界' })
		.refine((d) => new Set(d.answers).size === d.answers.length, { message: 'answers 重复' }),
	trueFalse: z.object({ expects: z.boolean() }),
	blank: z
		.object({
			text: z.string().min(1),
			blanks: z
				.array(z.object({ accepts: z.array(z.string().min(1)).min(1), normalize: z.array(NormalizerSchema).default([]) }))
				.min(1),
		})
		.refine((d) => countBlankMarkers(d.text) === d.blanks.length, { message: '空位数量与 blanks 长度不等' }),
	order: z
		.object({ items: z.array(z.string().min(1)).min(2), answer: z.array(z.number().int().nonnegative()).min(2) })
		.refine((d) => d.answer.length === d.items.length, { message: 'answer 与 items 长度不等' })
		.refine((d) => d.answer.every((i) => i < d.items.length), { message: 'answer 下标越界' })
		.refine((d) => new Set(d.answer).size === d.answer.length, { message: 'answer 不是排列' }),
	match: z
		.object({ pairs: z.array(z.tuple([z.string().min(1), z.string().min(1)])).min(2) })
		.refine((d) => new Set(d.pairs.map(([left]) => left)).size === d.pairs.length, { message: '左项重复' }),
	freeRecall: z.object({ reference: z.string().min(1), keywords: z.array(z.string()).default([]) }),
	reading: z.object({ passage: z.string().min(1), questions: z.array(z.unknown()).min(1) }),
} satisfies Record<QuestionKind, z.ZodType>

/** reading 子题在文件里的形状：外壳字段加 data，由 parseQuestion 处理。 */
export interface QuestionWire {
	readonly id: unknown
	readonly type: unknown
	readonly prompt: unknown
	readonly data: unknown
	readonly explain?: unknown
	readonly line: number
}

/** 载荷校验的上下文，只用来把 zod 错误翻译成带行号的 Issue。 */
export interface ParseContext {
	readonly conceptId: ConceptId
	readonly file: string
	readonly sectionIndex: number
	/** 嵌套一层时记录父题 id，Issue 消息里要能看出是哪道材料题。 */
	readonly parentQuestionId: QuestionId | null
}
